<template>
  <div class="container-fluid mt-4" :class="themeClass">

    <div class="card shadow-sm mb-3">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-toggles me-2"></i>Modo de operación de cajas</h5>
      </div>
      <div class="card-body">
        <div v-if="cargandoModo" class="text-muted small">Cargando...</div>
        <div v-else class="form-check form-switch">
          <input
            class="form-check-input"
            type="checkbox"
            id="chkCajaPorUsuario"
            v-model="cajaPorUsuario"
            :disabled="guardandoModo"
            @change="guardarModo"
          />
          <label class="form-check-label" for="chkCajaPorUsuario">
            <strong>{{ cajaPorUsuario ? 'Caja por usuario' : 'Caja compartida' }}</strong>
          </label>
        </div>
        <p class="text-muted small mt-2 mb-0">
          <span v-if="cajaPorUsuario">
            Cada usuario puede abrir su propia sesión en la misma caja física; varias personas
            pueden trabajar la misma caja al mismo tiempo, cada una con su propio saldo/sesión.
          </span>
          <span v-else>
            Una caja física solo admite una sesión abierta a la vez, sin importar quién la abrió
            (comportamiento actual). Nadie más podrá abrirla hasta que se cierre.
          </span>
        </p>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-gear me-2"></i>
          Configuración de Cajas
        </h5>
        <button v-if="puedeCrear" class="btn btn-primary btn-sm" @click="abrirModalNueva">
          <i class="bi bi-plus-circle me-1"></i> Nueva Caja
        </button>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center py-4">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else-if="error" class="alert alert-danger">
          <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
        </div>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th class="text-end">Saldo Inicial</th>
                  <th class="text-end">Saldo Mínimo</th>
                  <th>Estado</th>
                  <th>Sesión activa</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="cajas.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">
                    <i class="bi bi-inbox me-2"></i>No hay cajas configuradas
                  </td>
                </tr>
                <tr v-for="caja in cajas" :key="caja.id">
                  <td class="fw-semibold">{{ caja.nombre_caja }}</td>
                  <td><span class="badge bg-info text-dark">{{ caja.tipo }}</span></td>
                  <td class="text-muted small">{{ caja.descripcion || '—' }}</td>
                  <td class="text-end">${{ fmt(caja.saldo_inicial) }}</td>
                  <td class="text-end">${{ fmt(caja.saldo_minimo) }}</td>
                  <td>
                    <span :class="caja.activo == 1 ? 'badge bg-success' : 'badge bg-danger'">
                      {{ caja.activo == 1 ? 'Activa' : 'Inactiva' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="caja.sesion_activa" class="badge bg-warning text-dark">
                      <i class="bi bi-lock-fill me-1"></i>Abierta
                    </span>
                    <span v-else class="text-muted small">Libre</span>
                  </td>
                  <td>
                    <button v-if="puedeEditar" class="btn btn-sm btn-outline-warning me-1" @click="editarCaja(caja)">
                      <i class="bi bi-pencil"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL: Nueva / Editar caja
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="mostrarModal" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-cash-register me-2"></i>
              {{ editando ? 'Editar Caja' : 'Nueva Caja' }}
            </h5>
            <button type="button" class="btn-close" @click="cerrarModal"></button>
          </div>
          <form @submit.prevent="guardarCaja">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Nombre de la Caja <span class="text-danger">*</span></label>
                <input v-model="form.nombre_caja" type="text" class="form-control" required placeholder="Ej: Caja Principal" />
              </div>
              <div class="mb-3">
                <label class="form-label">Tipo</label>
                <select v-model="form.tipo" class="form-select">
                  <option value="principal">Principal</option>
                  <option value="punto_venta">Punto de venta</option>
                  <option value="sucursal">Sucursal</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Descripción</label>
                <textarea v-model="form.descripcion" class="form-control" rows="2" placeholder="Opcional..."></textarea>
              </div>
              <div class="row g-2">
                <div class="col-6">
                  <label class="form-label">Saldo Inicial ($)</label>
                  <input v-model.number="form.saldo_inicial" type="number" min="0" step="0.01" class="form-control" />
                </div>
                <div class="col-6">
                  <label class="form-label">Saldo Mínimo ($)</label>
                  <input v-model.number="form.saldo_minimo" type="number" min="0" step="0.01" class="form-control" />
                </div>
              </div>
              <div class="mt-3 form-check">
                <input v-model="form.activo" type="checkbox" class="form-check-input" id="chkActivo" :true-value="1" :false-value="0" />
                <label class="form-check-label" for="chkActivo">Caja activa</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrarModal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="guardando">
                <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                {{ guardando ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import notificationService from '@/services/notificationService'

export default {
  name: 'ClientCajasConfiguracion',

  data() {
    return {
      cargando:     false,
      error:        null,
      cajas:        [],
      cajaPorUsuario: false,
      cargandoModo: false,
      guardandoModo: false,
      mostrarModal: false,
      editando:     false,
      guardando:    false,
      form: {
        id:            null,
        nombre_caja:   '',
        tipo:          'punto_venta',
        descripcion:   '',
        saldo_inicial: 0,
        saldo_minimo:  0,
        activo:        1,
      },
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },
    puedeCrear() {
      return this.$store.getters.puedoHacer('/cajas/configuracion', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/cajas/configuracion', 'editar')
    },
  },

  async created() {
    await Promise.allSettled([this.cargar(), this.cargarModo()])
  },

  methods: {
    async cargarModo() {
      this.cargandoModo = true
      try {
        const { data } = await api.get('/configuracion/caja-modo')
        this.cajaPorUsuario = !!data?.data?.caja_por_usuario
      } catch {
        this.cajaPorUsuario = false
      } finally {
        this.cargandoModo = false
      }
    },

    async guardarModo() {
      this.guardandoModo = true
      try {
        await api.put('/configuracion/caja-modo', { caja_por_usuario: this.cajaPorUsuario })
        notificationService.success('Modo de caja actualizado correctamente')
      } catch (e) {
        this.cajaPorUsuario = !this.cajaPorUsuario
        notificationService.error(e.response?.data?.error || 'Error al guardar el modo de caja')
      } finally {
        this.guardandoModo = false
      }
    },

    async cargar() {
      this.cargando = true
      this.error    = null
      try {
        const { data } = await api.get('/cajas')
        this.cajas = data.cajas || []
      } catch {
        this.error = 'Error al cargar las cajas'
      } finally {
        this.cargando = false
      }
    },

    abrirModalNueva() {
      this.editando = false
      this.form = { id: null, nombre_caja: '', tipo: 'punto_venta', descripcion: '', saldo_inicial: 0, saldo_minimo: 0, activo: 1 }
      this.mostrarModal = true
    },

    editarCaja(caja) {
      this.editando = true
      this.form = {
        id:            caja.id,
        nombre_caja:   caja.nombre_caja,
        tipo:          caja.tipo || 'punto_venta',
        descripcion:   caja.descripcion || '',
        saldo_inicial: parseFloat(caja.saldo_inicial || 0),
        saldo_minimo:  parseFloat(caja.saldo_minimo  || 0),
        activo:        parseInt(caja.activo),
      }
      this.mostrarModal = true
    },

    cerrarModal() {
      this.mostrarModal = false
    },

    async guardarCaja() {
      this.guardando = true
      try {
        if (this.editando) {
          await api.put(`/cajas/${this.form.id}`, this.form)
          notificationService.success('Caja actualizada correctamente')
        } else {
          await api.post('/cajas', this.form)
          notificationService.success('Caja creada correctamente')
        }
        this.cerrarModal()
        await this.cargar()
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al guardar la caja'
        notificationService.error(msg)
      } finally {
        this.guardando = false
      }
    },

    fmt(val) { return parseFloat(val || 0).toFixed(2) },
  },
}
</script>
